// Capacity what-if for NXEdge Morgan Hill: load against capacity per machine
// group per week for WW45–WW52, with the levers a site planner would pull live —
// overtime, a second shift, another machine, the fixture size on the liner
// finish and bore machines, and moving a person to a machine they are trained
// on. The same lever panel as the Holman board (overtime / travelers / build
// speed), with machining levers. Detailing and CMM inspection are stations
// every part passes through, so their shortfall shows as hours, and a hire
// lever staffs the empty detailing bench. The plan, routings, time model and
// skills matrix are embedded (see model.ts), not read from the API.
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { Box, Button, Chip, IconButton, MenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { colors } from "../../../config/colors";
import { BoardShell } from "../../production-board/ui/components/board-shell";
import { allRows, changeWords, DEFAULT_BATCH, defaultLevers, emptySlots, groupOf, groupsChanged, HOURS_AT_OT, movesFor, OPERATIONS, OT_LEVELS, PEOPLE, peopleIn, SECOND_SHIFT_HOURS, unitHours, wagonWheelCatchUp, wagonWheelMaxUnits, WEEK_MONDAYS, WEEKS, WW_BEHIND_HOURS, WW_BEHIND_UNITS } from "./model"; // eslint-disable-line sort-imports
import { GroupId, GroupRow, Levers, OvertimeLevel } from "./model";

/** Companies the page is set up for (NXEdge on try.wilya.com). */
export const WHAT_IF_COMPANY_IDS = [226];
/** The drawer item the page highlights (use-drawer-controller). */
const MENU_ITEM_ID = 30;

const SEGMENTED_SX = {
	flexWrap: "wrap",
	"& .MuiToggleButton-root": { textTransform: "none", fontWeight: 700, px: 1.5, py: 0.5, lineHeight: 1.4 },
	"& .Mui-selected": { color: `${colors.button} !important`, background: `${colors.selected} !important` },
};

const fmt1 = (n: number): string => n.toFixed(1).replace(/\.0$/, "");
const article = (word: string): string => (/^[aeiou]/i.test(word) ? "an" : "a");
const capital = (word: string): string => word.charAt(0).toUpperCase() + word.slice(1);

/** One lever: its name and what it means, the control, and its effect. */
const LeverRow: React.FC<{ title: string; sub: string; effect: string; changed: boolean; children: React.ReactNode }> = ({ title, sub, effect, changed, children }) => (
	<Box
		sx={{
			display: "grid",
			gridTemplateColumns: { xs: "1fr", md: "280px minmax(0, 1fr) 200px" },
			alignItems: "center",
			columnGap: 3,
			rowGap: 1,
			px: 2,
			py: 1.5,
			borderTop: `1px solid ${colors.border}`,
		}}
	>
		<Box>
			<Typography variant='body2' fontWeight={700}>
				{title}
			</Typography>
			<Typography variant='caption' color={colors.disclaimer}>
				{sub}
			</Typography>
		</Box>
		<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", minWidth: 0 }}>{children}</Box>
		<Typography variant='body2' fontWeight={700} sx={{ textAlign: { md: "right" }, color: changed ? colors.green : colors.disclaimer, fontVariantNumeric: "tabular-nums" }}>
			{effect}
		</Typography>
	</Box>
);

export const Stepper: React.FC<{ name: string; value: number; min: number; max: number; onStep: (name: string, next: number) => void }> = ({ name, value, min, max, onStep }) => {
	const fewer = useCallback(() => onStep(name, Math.max(min, value - 1)), [name, value, min, onStep]);
	const more = useCallback(() => onStep(name, Math.min(max, value + 1)), [name, value, max, onStep]);
	return (
		<Box sx={{ display: "flex", alignItems: "center", border: `1px solid ${colors.border}`, borderRadius: "4px", background: colors.light }}>
			<IconButton size='small' aria-label={`fewer ${name}`} onClick={fewer} disabled={value <= min}>
				<RemoveIcon fontSize='small' />
			</IconButton>
			<Typography sx={{ width: 36, textAlign: "center", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
			<IconButton size='small' aria-label={`more ${name}`} onClick={more} disabled={value >= max}>
				<AddIcon fontSize='small' />
			</IconButton>
		</Box>
	);
};

/** load / capacity for one group-week, tinted by how far over it is. */
const WeekCell: React.FC<{ load: number; capacity: number; utilization: number; over: boolean; unitsShort: number; hoursShort: number; shared: boolean }> = ({ load, capacity, utilization, over, unitsShort, hoursShort, shared }) => (
	<Box
		sx={{
			px: 1,
			py: 0.75,
			borderLeft: `1px solid ${colors.border}`,
			background: over ? colors.errorBox : utilization >= 90 ? colors.warningBox : "transparent",
			minWidth: 0,
		}}
	>
		<Typography variant='body2' sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", color: over ? colors.errorStrong : colors.primary }}>
			{utilization}%
		</Typography>
		<Typography variant='caption' sx={{ color: colors.disclaimer, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
			{fmt1(load)} / {fmt1(capacity)} h
		</Typography>
		{over ? (
			<Typography variant='caption' sx={{ display: "block", color: colors.errorStrong, fontWeight: 700 }}>
				{shared ? `${fmt1(hoursShort)} h short` : `${unitsShort} short`}
			</Typography>
		) : null}
	</Box>
);

const RowSummary: React.FC<{ row: GroupRow }> = ({ row }) => (
	<Box sx={{ px: 1.5, py: 0.75, borderLeft: `1px solid ${colors.border}` }}>
		<Typography variant='body2' sx={{ fontWeight: 700, color: row.weeksOver > 0 ? colors.errorStrong : colors.green }}>
			{row.weeksOver > 0 ? `${row.weeksOver} of ${WEEKS.length} weeks over` : "Fits every week"}
		</Typography>
		<Typography variant='caption' sx={{ color: colors.disclaimer }}>
			{row.capacity.operators} of {row.capacity.machines} {row.capacity.machines > 1 ? row.group.units : row.group.unit} staffed · {fmt1(row.capacity.hoursPerOperator)} h each
			{row.capacity.secondShiftHours > 0 ? ` · +${fmt1(row.capacity.secondShiftHours)} h 2nd shift` : ""}
		</Typography>
	</Box>
);

const WhatIf: React.FC = () => {
	const companyId = useSelector((state: any) => state.user?.userData?.companyId); // eslint-disable-line @typescript-eslint/no-explicit-any
	const [levers, setLevers] = useState<Levers>(defaultLevers);
	const [group, setGroup] = useState<GroupId>("CMM");
	const [movePerson, setMovePerson] = useState<string>("");
	const [site, setSite] = useState("morgan-hill");
	const [period, setPeriod] = useState("ww45-52");

	const rows = useMemo(() => allRows(levers), [levers]);
	const row = rows.find((r) => r.group.id === group) as GroupRow;
	const changed = useMemo(() => groupsChanged(levers), [levers]);
	const catchUp = useMemo(() => wagonWheelCatchUp(levers), [levers]);
	const wwMax = useMemo(() => wagonWheelMaxUnits(levers), [levers]);
	const people = useMemo(() => peopleIn(group, levers), [group, levers]);
	const person = PEOPLE.find((p) => p.id === movePerson);
	const targets = useMemo(() => (person ? movesFor(person, levers) : []), [person, levers]);
	const batchOp = OPERATIONS.find((op) => op.group === group && op.family === row.group.countsFamily);

	const handleSite = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setSite(e.target.value), []);
	const handlePeriod = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setPeriod(e.target.value), []);
	const handleGroup = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setGroup(e.target.value as GroupId), []);
	const handleChip = useCallback((e: React.MouseEvent<HTMLElement>) => {
		const next = e.currentTarget.dataset.group as GroupId | undefined;
		if (next) setGroup(next);
	}, []);
	const handleOvertime = useCallback(
		(_: React.MouseEvent<HTMLElement>, next: OvertimeLevel | null) => {
			if (next === null) return;
			setLevers((l) => ({ ...l, overtime: { ...l.overtime, [group]: next } }));
		},
		[group]
	);
	const handleStep = useCallback(
		(name: string, next: number) => setLevers((l) => ({ ...l, [name]: { ...(l as unknown as Record<string, Record<GroupId, number>>)[name], [group]: next } })),
		[group]
	);
	const handleMovePerson = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setMovePerson(e.target.value), []);
	const handleMoveTo = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const to = e.target.value as GroupId;
			if (!movePerson || !to) return;
			setLevers((l) => ({ ...l, moves: [...l.moves.filter((m) => m.personId !== movePerson), { personId: movePerson, to }] }));
			setMovePerson("");
		},
		[movePerson]
	);
	const handleUndoMove = useCallback((e: React.MouseEvent<HTMLElement>) => {
		const id = e.currentTarget.dataset.person;
		if (id) setLevers((l) => ({ ...l, moves: l.moves.filter((m) => m.personId !== id) }));
	}, []);
	const handleReset = useCallback(() => {
		setLevers(defaultLevers());
		setMovePerson("");
	}, []);

	if (!WHAT_IF_COMPANY_IDS.includes(Number(companyId))) {
		return (
			<BoardShell menuId={MENU_ITEM_ID}>
				<Box sx={{ p: 4 }}>
					<Typography variant='h6'>This what-if page is set up for NXEdge Morgan Hill.</Typography>
				</Box>
			</BoardShell>
		);
	}

	const ot = levers.overtime[group];
	const otExtra = (HOURS_AT_OT[ot] - HOURS_AT_OT[0]) * row.capacity.operators;
	const batch = levers.batch[group];
	const perUnitNow = batchOp ? unitHours(batchOp, batch) : 0;
	const perUnitDefault = batchOp ? unitHours(batchOp, DEFAULT_BATCH) : 0;
	const movedHere = levers.moves.filter((m) => m.to === group);
	const hired = levers.extraPeople[group];
	const free = emptySlots(group, levers);
	const { unit, units, role } = row.group;

	return (
		<BoardShell menuId={MENU_ITEM_ID}>
		<Box sx={{ p: { xs: 2, md: 3 }, overflowY: "auto", minHeight: 0, flex: 1 }}>
			<Stack direction='row' alignItems='center' gap={1.5} sx={{ mb: 2, flexWrap: { xs: "wrap", md: "nowrap" } }}>
				<Typography variant='h5' fontWeight={700} sx={{ mr: 1, whiteSpace: "nowrap" }}>
					Capacity what-if
				</Typography>
				<TextField select size='small' label='Site' value={site} onChange={handleSite} sx={{ minWidth: 240, background: colors.light }}>
					<MenuItem value='morgan-hill'>NXEdge · Morgan Hill, CA</MenuItem>
				</TextField>
				<TextField select size='small' label='Period' value={period} onChange={handlePeriod} sx={{ minWidth: 210, background: colors.light }}>
					<MenuItem value='ww45-52'>WW45–WW52 · Nov 2 to Dec 27</MenuItem>
				</TextField>
			</Stack>

			<Box sx={{ border: `1px solid ${colors.border}`, borderRadius: "8px", overflow: "hidden", background: colors.light, mb: 3 }}>
				<Stack direction='row' gap={1.5} alignItems='center' flexWrap='wrap' sx={{ px: 2, py: 1.5, background: colors.greyLight }}>
					<TextField select size='small' label='Work center' value={group} onChange={handleGroup} sx={{ minWidth: 300, background: colors.light }}>
						{rows.map((r) => (
							<MenuItem key={r.group.id} value={r.group.id}>
								{r.group.name}
							</MenuItem>
						))}
					</TextField>
					{changed.length > 0 ? (
						changed.map((id) => (
							<Chip
								key={id}
								data-group={id}
								clickable
								onClick={handleChip}
								variant={id === group ? "filled" : "outlined"}
								label={`${groupOf(id).short}: ${changeWords(id, levers).join(" · ")}`}
								sx={{ fontWeight: 700, color: colors.green, borderColor: colors.green, background: id === group ? colors.successBox : colors.light }}
							/>
						))
					) : (
						<Typography variant='body2' color={colors.disclaimer}>
							No changes yet: the plan as it stands, one shift, no overtime.
						</Typography>
					)}
				</Stack>

				<LeverRow
					title='Overtime'
					sub={`${row.capacity.operators} ${role}${row.capacity.operators === 1 ? "" : "s"} on ${row.group.short}; EnPro's hours per person-week`}
					effect={ot > 0 ? `+${fmt1(otExtra)} h a week` : "No change"}
					changed={ot > 0}
				>
					<ToggleButtonGroup exclusive size='small' value={ot} onChange={handleOvertime} aria-label='Overtime' sx={SEGMENTED_SX}>
						{OT_LEVELS.map((level) => (
							<ToggleButton key={level} value={level}>
								{level === 0 ? "None" : `${level}% · ${HOURS_AT_OT[level]} h`}
							</ToggleButton>
						))}
					</ToggleButtonGroup>
				</LeverRow>

				<LeverRow
					title='Second shift'
					sub={`${capital(units)} that also run a second shift, ${SECOND_SHIFT_HOURS} h each with its own ${role}`}
					effect={levers.secondShift[group] > 0 ? `+${fmt1(Math.min(levers.secondShift[group], row.capacity.machines) * SECOND_SHIFT_HOURS)} h a week` : "No change"}
					changed={levers.secondShift[group] > 0}
				>
					<Stepper name='secondShift' value={levers.secondShift[group]} min={0} max={row.capacity.machines} onStep={handleStep} />
					<Typography variant='caption' color={colors.disclaimer}>
						of {row.capacity.machines} {row.capacity.machines > 1 ? units : unit}
					</Typography>
				</LeverRow>

				<LeverRow
					title={`Add ${article(unit)} ${unit}`}
					sub={`Another ${unit} with ${article(role)} ${role} on the day shift`}
					effect={levers.extraMachines[group] > 0 ? `+${fmt1(levers.extraMachines[group] * HOURS_AT_OT[ot])} h a week` : "No change"}
					changed={levers.extraMachines[group] > 0}
				>
					<Stepper name='extraMachines' value={levers.extraMachines[group]} min={0} max={3} onStep={handleStep} />
				</LeverRow>

				<LeverRow
					title={`Hire ${article(role)} ${role}`}
					sub={free + hired > 0 ? `Staff ${article(unit)} ${unit} that has nobody on it today` : `Every ${unit} on ${row.group.short} is staffed: add ${article(unit)} ${unit} or a second shift instead`}
					effect={hired > 0 ? `+${fmt1(hired * HOURS_AT_OT[ot])} h a week` : "No change"}
					changed={hired > 0}
				>
					<Stepper name='extraPeople' value={hired} min={0} max={hired + free} onStep={handleStep} />
					<Typography variant='caption' color={colors.disclaimer}>
						{free} empty {free === 1 ? unit : units}
					</Typography>
				</LeverRow>

				{row.group.batchable && batchOp ? (
					<LeverRow
						title='Batch size'
						sub={`Liners per fixture on ${row.group.short}: setup ${batchOp.setupMin} min and run ${fmt1(batchOp.runMin / 60)} h per fixture`}
						effect={batch !== DEFAULT_BATCH ? `${fmt1(perUnitDefault)} h → ${perUnitNow.toFixed(2)} h per liner` : `${fmt1(perUnitNow)} h per liner`}
						changed={batch !== DEFAULT_BATCH}
					>
						<Stepper name='batch' value={batch} min={3} max={8} onStep={handleStep} />
					</LeverRow>
				) : null}

				<LeverRow
					title='Move a person'
					sub='Only to a work center the skills matrix says they are trained on'
					effect={movedHere.length > 0 ? `${movedHere.length} moved in` : "No change"}
					changed={movedHere.length > 0}
				>
					<TextField select size='small' label='Person' value={movePerson} onChange={handleMovePerson} sx={{ minWidth: 220 }}>
						{PEOPLE.map((p) => (
							<MenuItem key={p.id} value={p.id}>
								{p.name} · {groupOf(levers.moves.find((m) => m.personId === p.id)?.to ?? p.home).short}
							</MenuItem>
						))}
					</TextField>
					{person ? (
						<TextField select size='small' label='Move to' value='' onChange={handleMoveTo} sx={{ minWidth: 260 }} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
							{targets.length === 0 ? <MenuItem disabled value=''>Not trained anywhere else</MenuItem> : null}
							{targets.map((id) => (
								<MenuItem key={id} value={id}>
									{groupOf(id).name} · level {person.skills[id]}
								</MenuItem>
							))}
						</TextField>
					) : null}
					{levers.moves.map((m) => (
						<Chip key={m.personId} data-person={m.personId} onDelete={handleUndoMove} label={`${PEOPLE.find((p) => p.id === m.personId)?.name} → ${groupOf(m.to).short}`} size='small' />
					))}
					<Typography variant='caption' color={colors.disclaimer}>
						On {row.group.short} now: {people.map((p) => p.name.split(" ")[0]).join(", ") || "nobody"}
					</Typography>
				</LeverRow>

				<Stack direction='row' gap={1.5} alignItems='center' sx={{ px: 2, py: 1.5, borderTop: `1px solid ${colors.border}`, background: colors.greyLight }}>
					<Box sx={{ flex: 1 }} />
					<Button onClick={handleReset} sx={{ textTransform: "none", fontWeight: 700 }}>
						Reset
					</Button>
				</Stack>
			</Box>

			<Box sx={{ border: `1px solid ${colors.border}`, borderRadius: "8px", overflow: "auto", background: colors.light, mb: 3 }}>
				<Box sx={{ display: "grid", gridTemplateColumns: `260px repeat(${WEEKS.length}, minmax(96px, 1fr)) 220px`, minWidth: 1200 }}>
					<Box sx={{ px: 1.5, py: 1, background: colors.greyLight }}>
						<Typography variant='caption' fontWeight={700} color={colors.disclaimer}>
							LOAD AGAINST CAPACITY · HOURS A WEEK
						</Typography>
					</Box>
					{WEEKS.map((week) => (
						<Box key={week} sx={{ px: 1, py: 1, background: colors.greyLight, borderLeft: `1px solid ${colors.border}` }}>
							<Typography variant='caption' fontWeight={700}>
								WW{week}
							</Typography>
							<Typography variant='caption' sx={{ display: "block", color: colors.disclaimer }}>
								{WEEK_MONDAYS[week].slice(5).replace("-", "/")}
							</Typography>
						</Box>
					))}
					<Box sx={{ px: 1.5, py: 1, background: colors.greyLight, borderLeft: `1px solid ${colors.border}` }}>
						<Typography variant='caption' fontWeight={700} color={colors.disclaimer}>
							AS SET
						</Typography>
					</Box>
					{rows.map((r) => (
						<React.Fragment key={r.group.id}>
							<Box
								data-group={r.group.id}
								onClick={handleChip}
								sx={{ px: 1.5, py: 0.75, borderTop: `1px solid ${colors.border}`, cursor: "pointer", background: r.group.id === group ? colors.selected : "transparent" }}
							>
								<Typography variant='body2' fontWeight={700}>
									{r.group.name}
								</Typography>
								<Typography variant='caption' color={colors.disclaimer}>
									{OPERATIONS.filter((op) => op.group === r.group.id)
										.map((op) => `${op.family === "UL" ? "Upper" : op.family === "LL" ? "Lower" : op.family === "PS" ? "Plasma" : "Wagon"} ${fmt1(unitHours(op, levers.batch[r.group.id]) * 60)} min/unit${op.source === "assumed" ? " (assumed)" : ""}`)
										.join(" · ")}
								</Typography>
							</Box>
							{r.weeks.map((w) => (
								<Box key={w.week} sx={{ borderTop: `1px solid ${colors.border}` }}>
									<WeekCell load={w.load} capacity={w.capacity} utilization={w.utilization} over={w.over} unitsShort={w.unitsShort} hoursShort={w.hoursShort} shared={r.group.shared} />
								</Box>
							))}
							<Box sx={{ borderTop: `1px solid ${colors.border}` }}>
								<RowSummary row={r} />
							</Box>
						</React.Fragment>
					))}
				</Box>
			</Box>

			<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 720px)" }, gap: 3 }}>
				<Box sx={{ border: `1px solid ${colors.border}`, borderRadius: "8px", background: colors.light, p: 2 }}>
					<Typography variant='subtitle1' fontWeight={700}>
						Wagon Wheel catch-up
					</Typography>
					<Typography variant='body2' color={colors.disclaimer} sx={{ mb: 1.5 }}>
						{WW_BEHIND_UNITS} units behind plan at the end of WW43, {fmt1(WW_BEHIND_HOURS)} h of work at 70 min a wheel. The plan of 75 a week takes {fmt1(75 * (70 / 60))} h; as set, the cell makes at most {wwMax} a week.
					</Typography>
					{catchUp.map((option) => (
						<Box key={option.label} sx={{ display: "grid", gridTemplateColumns: "1fr 110px 110px", py: 0.75, borderTop: `1px solid ${colors.border}` }}>
							<Typography variant='body2'>{option.label}</Typography>
							<Typography variant='body2' sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: colors.disclaimer }}>
								{option.surplus > 0 ? `+${fmt1(option.surplus)} h/wk` : `${fmt1(option.surplus)} h/wk`}
							</Typography>
							<Typography variant='body2' sx={{ textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums", color: option.weeks === null ? colors.errorStrong : colors.green }}>
								{option.weeks === null ? "never" : `${fmt1(option.weeks)} weeks`}
							</Typography>
						</Box>
					))}
				</Box>
			</Box>
		</Box>
		</BoardShell>
	);
};

export default WhatIf;
